<template>
  <input
    v-bind="inputAttrs"
    :type="resolvedType"
    :placeholder="placeholder || ''"
    :value="modelValue"
    :readonly="Boolean(readonly)"
    :inputmode="resolvedInputMode"
    :maxlength="resolvedMaxLength"
    :pattern="resolvedPattern"
    class="ui-input"
    @input="onInput"
    @blur="onBlur"
  />
</template>

<script setup lang="ts">
import { useAutoLabel } from '@/shared/lib/autoLabel'
import { computed, useAttrs } from 'vue'
import { formatRussianPhone } from '@/shared/lib/phone'
defineOptions({ inheritAttrs: false })

type InputFilter = 'digits' | 'integer' | 'decimal' | 'phone'

useAutoLabel()

const props = withDefaults(
  defineProps<{
    modelValue: string | number
    type?: string
    placeholder?: string
    readonly?: boolean
    inputFilter?: InputFilter
  }>(),
  {
    type: 'text',
    placeholder: '',
    readonly: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const attrs = useAttrs()

const inputAttrs = computed(() => {
  if (!props.inputFilter) return attrs

  const { maxlength: _maxlength, ...rest } = attrs
  return rest
})

const resolvedType = computed(() => (props.inputFilter ? 'text' : props.type || 'text'))

function getNormalizedMaxLength(): string | number | undefined {
  const maxlength = attrs['maxlength']
  if (typeof maxlength === 'number') return maxlength
  if (typeof maxlength === 'string') {
    const parsed = Number.parseInt(maxlength, 10)
    return Number.isFinite(parsed) && parsed > 0 ? maxlength : undefined
  }

  return undefined
}

const sanitizeMaxLength = computed(() => {
  const maxlength = getNormalizedMaxLength()
  if (typeof maxlength === 'number') return maxlength
  if (typeof maxlength === 'string') return Number.parseInt(maxlength, 10)

  return undefined
})

const resolvedMaxLength = computed(() => (props.inputFilter ? undefined : getNormalizedMaxLength()))

const resolvedInputMode = computed(() => {
  if (props.inputFilter === 'decimal') return 'decimal'
  if (
    props.inputFilter === 'digits' ||
    props.inputFilter === 'integer' ||
    props.inputFilter === 'phone'
  ) {
    return 'numeric'
  }

  return undefined
})

const resolvedPattern = computed(() => {
  if (props.inputFilter === 'decimal') return '[0-9]+([\\.,][0-9]+)?'
  if (props.inputFilter === 'digits' || props.inputFilter === 'integer') return '\\d*'
  if (props.inputFilter === 'phone') return '[0-9+()\\-\\s]*'

  return undefined
})

function onInput(event: Event) {
  if (props.readonly) return
  const target = event.target as HTMLInputElement
  const value = sanitizeValue(target.value)
  if (target.value !== value) {
    target.value = value
  }

  emit('update:modelValue', value)
}

function onBlur(event: FocusEvent) {
  if (props.readonly || props.inputFilter !== 'phone') return

  const target = event.target as HTMLInputElement
  const formatted_value = formatRussianPhone(target.value)
  if (formatted_value !== target.value) {
    target.value = formatted_value
    emit('update:modelValue', formatted_value)
  }
}

function sanitizeValue(value: string): string {
  let sanitized_value: string

  switch (props.inputFilter) {
    case 'digits':
    case 'integer':
      sanitized_value = value.replace(/\D/g, '')
      break
    case 'decimal':
      sanitized_value = sanitizeDecimal(value)
      break
    case 'phone':
      sanitized_value = value.replace(/[^0-9+()\-\s]/g, '')
      break
    default:
      sanitized_value = value
  }

  return sanitizeMaxLength.value
    ? sanitized_value.slice(0, sanitizeMaxLength.value)
    : sanitized_value
}

function sanitizeDecimal(value: string): string {
  const normalized = value.replace(',', '.').replace(/[^0-9.]/g, '')
  const [integer_part = '', ...fraction_parts] = normalized.split('.')
  const fraction = fraction_parts.join('')

  return fraction_parts.length > 0 ? `${integer_part}.${fraction}` : integer_part
}
</script>

<style scoped>
.ui-input {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  width: 100%;
  font: inherit;
}

.ui-input:read-only {
  background: #f8fafc;
  color: #586578;
  cursor: default;
}
</style>
